import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { promptsListQueryOptions, promptsUsageQueryOptions, promptsKeys } from "@/lib/queries";
import type { PromptsListFilters, PromptDto } from "@/lib/queries";
import { PromptsKpiStrip } from "./PromptsKpiStrip";
import { PromptsTable } from "./PromptsTable";
import { PromptsTableToolbar } from "./PromptsTableToolbar";
import { PromptEditorSheet } from "./PromptEditorSheet";
import { PromptSearchReplacePanel } from "./PromptSearchReplacePanel";
import { DuplicatePromptDialog } from "./DuplicatePromptDialog";
import { DeletePromptDialog } from "./DeletePromptDialog";

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

interface PromptsPageProps {
  /** Call site the list opens filtered to; the user can clear it like any other filter. */
  initialUsedIn?: string;
}

export function PromptsPage({ initialUsedIn }: PromptsPageProps = {}) {
  const [filters, setFilters] = useState<PromptsListFilters>(() =>
    initialUsedIn ? { usedIn: initialUsedIn } : {},
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [editorLine, setEditorLine] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFind, setReplaceFind] = useState("");
  const [duplicatePrompt, setDuplicatePrompt] = useState<PromptDto | null>(null);
  const [deletePrompt, setDeletePrompt] = useState<PromptDto | null>(null);
  const queryClient = useQueryClient();

  const queryFilters = useMemo<PromptsListFilters>(
    () => ({ ...filters, q: debouncedSearch.trim() || undefined }),
    [filters, debouncedSearch],
  );
  const hasSearch = debouncedSearch.trim().length > 0;
  const { data: prompts = [], isLoading } = useQuery(promptsListQueryOptions(queryFilters));
  const { data: usageResponse, isLoading: usageLoading } = useQuery(promptsUsageQueryOptions());
  const usageByPromptId = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof usageResponse>["items"][number]
    >();
    for (const row of usageResponse?.items ?? []) {
      map.set(row.promptId, row);
    }
    return map;
  }, [usageResponse]);
  const scopedQuery = useQuery({
    ...promptsListQueryOptions(filters),
    enabled: hasSearch,
  });

  const visiblePrompts = prompts;
  const totalCount =
    hasSearch && scopedQuery.data !== undefined ? scopedQuery.data.length : prompts.length;

  const selectedPrompt = prompts.find((p) => p.id === selectedPromptId) ?? null;

  const handleRowClick = (prompt: PromptDto) => {
    setEditorLine(null);
    setSelectedPromptId(prompt.id);
  };

  const openReplace = (find = "") => {
    setReplaceFind(find);
    setReplaceOpen(true);
  };

  const handleClose = () => {
    setSelectedPromptId(null);
    setCreateOpen(false);
  };

  const handleSaved = () => {
    void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
  };

  return (
    <div className="flex h-full flex-col gap-4 pt-4">
      <PromptsTableToolbar
        filters={filters}
        onFiltersChange={setFilters}
        search={search}
        onSearchChange={setSearch}
        visibleCount={visiblePrompts.length}
        totalCount={totalCount}
        onCreateNew={() => setCreateOpen(true)}
        onOpenReplace={() => openReplace()}
      />

      <PromptsKpiStrip
        prompts={visiblePrompts}
        usageByPromptId={usageByPromptId}
        isLoading={isLoading || usageLoading}
      />

      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 min-w-0 flex-1 px-4 pb-4">
          <PromptsTable
            prompts={visiblePrompts}
            usageByPromptId={usageByPromptId}
            isLoading={isLoading || usageLoading}
            searchQuery={search}
            onRowClick={handleRowClick}
            onDuplicate={setDuplicatePrompt}
            onDelete={setDeletePrompt}
          />
        </div>
        {replaceOpen ? (
          <PromptSearchReplacePanel
            initialFind={replaceFind}
            onClose={() => setReplaceOpen(false)}
            onOpenMatch={(promptId, line) => {
              setCreateOpen(false);
              setEditorLine(line);
              setSelectedPromptId(promptId);
            }}
          />
        ) : null}
      </div>

      <PromptEditorSheet
        open={!!selectedPromptId || createOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        promptId={selectedPromptId}
        prompt={selectedPrompt}
        mode={createOpen ? "create" : "edit"}
        revealLine={editorLine}
        onSaved={handleSaved}
        onDuplicate={setDuplicatePrompt}
        onDelete={setDeletePrompt}
        onOpenPrompt={(promptId) => {
          setEditorLine(null);
          setSelectedPromptId(promptId);
        }}
        onOpenReplace={openReplace}
      />

      {duplicatePrompt && (
        <DuplicatePromptDialog
          key={duplicatePrompt.id}
          prompt={duplicatePrompt}
          open
          onOpenChange={(open) => {
            if (!open) setDuplicatePrompt(null);
          }}
        />
      )}

      {deletePrompt && (
        <DeletePromptDialog
          key={deletePrompt.id}
          prompt={deletePrompt}
          open
          onOpenChange={(open) => {
            if (!open) setDeletePrompt(null);
          }}
          onDeleted={() => {
            if (selectedPromptId === deletePrompt.id) {
              setSelectedPromptId(null);
            }
          }}
        />
      )}
    </div>
  );
}
